import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatPrefsSummary, useAdvisor } from '@/lib/advisor-context';
import { primaryVisitProject } from '@/lib/visit-plan';

function builderLabel(project: { name: string; builder?: string }): string {
  if (project.builder && project.builder !== 'Naya') return project.builder;
  const lower = project.name.toLowerCase();
  if (lower.includes('brigade') || lower.includes('buena')) return 'Brigade Group';
  return 'Lokations';
}

export function RevealPage() {
  const { confirmReveal, prefs, shortlist, matchProjects, visitQueue, bookedVisits, mode } =
    useAdvisor();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const project = useMemo(() => {
    const fromShortlist = primaryVisitProject(shortlist);
    if (fromShortlist) return fromShortlist;
    const activeId = visitQueue?.active?.project_id;
    if (activeId) {
      return (
        matchProjects.find((p) => p.id === activeId) ??
        shortlist.find((p) => p.id === activeId) ??
        null
      );
    }
    const booked = bookedVisits[0];
    if (booked) {
      return (
        matchProjects.find((p) => p.id === booked.projectId) ?? {
          id: booked.projectId,
          name: booked.projectName,
          builder: 'Naya',
          location: '',
          price: '',
          bhk: '',
          fit: '',
          accent: '',
        }
      );
    }
    return matchProjects[0] ?? null;
  }, [bookedVisits, matchProjects, shortlist, visitQueue?.active?.project_id]);

  const builder = project ? builderLabel(project) : 'the builder';
  const visitLabel =
    visitQueue?.proposed_label?.trim() ||
    bookedVisits.find((v) => v.projectId === project?.id)?.label ||
    'your visit day';

  const handleConfirm = async () => {
    if (!project) return;
    const digits = phone.replace(/\D/g, '');
    if (name.trim().length < 2) {
      setError('Add your name so the site team knows who to expect.');
      return;
    }
    if (digits.length < 10) {
      setError('Enter a valid 10-digit WhatsApp number.');
      return;
    }
    setError('');
    setBusy(true);
    const result = await confirmReveal({
      project,
      buyerName: name.trim(),
      buyerPhone: phone.trim(),
      visitLabel,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'Could not share contact — try again.');
      return;
    }
    navigate('/');
  };

  if (!project) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="max-w-sm text-center">
          <Shield className="mx-auto mb-4 h-9 w-9 text-muted-foreground/60" strokeWidth={1.25} />
          <h2 className="font-serif text-xl font-semibold">Nothing to reveal yet</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Shortlist projects and plan a visit first.
          </p>
          <Button className="mt-8" asChild>
            <Link to="/">Back to chat</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-center px-6 py-12">
      <div className="w-full max-w-md rounded-lg border bg-card p-8 shadow-sm">
        <div className="text-center">
          <div className="mx-auto mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-privacy-soft text-privacy">
            <Shield className="h-5 w-5" />
          </div>
          <h2 className="font-serif text-xl font-semibold leading-snug">
            Share contact for this visit?
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Only <span className="font-medium text-foreground">{builder}</span> receives your details
            for the visit at{' '}
            <span className="font-medium text-foreground">{project.name}</span>. No other builder is
            notified.
          </p>
        </div>

        <dl className="mt-6 space-y-3.5 border-t pt-6 text-sm">
          <div>
            <dt className="advisor-stat-label">Visit</dt>
            <dd className="mt-1 font-medium">
              {visitLabel} · {project.name}
              {project.location ? `, ${project.location}` : ''}
            </dd>
          </div>
          <div>
            <dt className="advisor-stat-label">Context</dt>
            <dd className="mt-1 font-medium">
              {shortlist.length} shortlisted
              {prefs.location ? ` · prefers ${prefs.location}` : ''}
              {formatPrefsSummary(prefs) ? ` · ${formatPrefsSummary(prefs)}` : ''}
            </dd>
          </div>
          {mode === 'live' ? (
            <div>
              <dt className="advisor-stat-label">Handoff</dt>
              <dd className="mt-1 text-muted-foreground">
                Creates a Desk lead on the source builder with channel advisor_web.
              </dd>
            </div>
          ) : null}
        </dl>

        <div className="mt-5 space-y-3">
          <div>
            <label className="advisor-stat-label" htmlFor="reveal-name">
              Your name
            </label>
            <input
              id="reveal-name"
              className="mt-1 w-full rounded-md border bg-muted/40 px-3 py-2 text-sm"
              autoComplete="name"
              placeholder="e.g. Priya"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="advisor-stat-label" htmlFor="reveal-phone">
              WhatsApp number
            </label>
            <input
              id="reveal-phone"
              className="mt-1 w-full rounded-md border bg-muted/40 px-3 py-2 text-sm"
              autoComplete="tel"
              inputMode="tel"
              placeholder="+91 98XXX XXXXX"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <div className="mt-6 space-y-2">
          <Button className="w-full py-5" onClick={() => void handleConfirm()} disabled={busy}>
            {busy ? 'Sharing…' : 'Confirm — this visit only'}
          </Button>
          <Button variant="outline" className="w-full" asChild>
            <Link to="/">Not yet</Link>
          </Button>
        </div>

        <p className="mt-5 text-center text-[11px] leading-relaxed text-muted-foreground">
          You can continue exploring other projects anonymously.
        </p>
      </div>
    </div>
  );
}
